"use client"

import { RefObject, useEffect, useState } from "react"

/**
 * Android Chrome은 MediaCodec HW 디코더 인스턴스가 기기별 4~16개로 제한되며,
 * 초과 할당 시 SW 폴백 없이 해당 video 파이프라인이 terminal error로 정지한다
 * (ended/timeupdate 이벤트도 멈춰 JS 폴백 불가 → loop 멈춤으로 발현).
 * 동시 재생 수를 이 상한 아래로 유지하기 위한 전역 budget.
 *
 * 상한 8은 한 화면에 동시 노출 가능한 최대 카드 수 기준 — 보이는 카드가
 * poster로 멈춰 있으면 버그로 오인되므로, 상한은 노출 수 이상으로 잡고
 * 디코더가 더 적은 기기는 디코드 실패 관측 시 적응적으로 축소한다.
 */
const ANDROID_MAX_CONCURRENT_PLAYBACK = 8
/** 적응 축소 하한 — 이 밑으로는 줄이지 않는다 */
const ANDROID_MIN_CONCURRENT_PLAYBACK = 3
/** 카드가 이 비율 이상 보이면 재생 시작 */
const VISIBILITY_PLAY_RATIO = 0.3
/**
 * 이 비율 미만으로 가려져야 정지 (재생 시작 기준과 분리된 히스테리시스 —
 * 경계에서 스크롤이 멈췄을 때 load/detach가 반복되는 플래핑 방지)
 */
const VISIBILITY_STOP_RATIO = 0.15

function isAndroid(): boolean {
	return typeof navigator !== "undefined" && /android/i.test(navigator.userAgent)
}

interface PlaybackWaiter {
	token: symbol
	grant: () => void
}

/**
 * 전역 동시 재생 budget 매니저 (모듈 싱글톤).
 * Android에서만 상한을 적용하고 그 외 플랫폼(데스크탑/iOS)은 무제한 —
 * 버그가 없는 플랫폼의 UX를 바꾸지 않는다.
 */
class VideoPlaybackBudgetManager {
	private limit = isAndroid() ? ANDROID_MAX_CONCURRENT_PLAYBACK : Infinity
	private readonly active = new Set<symbol>()
	private waiters: PlaybackWaiter[] = []

	/**
	 * 디코드 실패(MEDIA_ERR_DECODE) 관측 시 상한을 현재 활성 수 미만으로 축소.
	 * 디코더 인스턴스가 적은 기기에서 상한 8이 과한 경우의 적응 경로 —
	 * 이미 재생 중인 video는 유지하고 자연 반납으로 수렴시킨다.
	 */
	notifyDecodeFailure(): void {
		if (this.limit === Infinity) return
		const shrunk = Math.max(this.active.size - 1, ANDROID_MIN_CONCURRENT_PLAYBACK)
		if (shrunk < this.limit) {
			this.limit = shrunk
		}
	}

	/** 슬롯 요청. 즉시 획득 시 true, 대기열 등록 시 false (해제 시 grant 콜백 호출) */
	request(token: symbol, grant: () => void): boolean {
		if (this.active.has(token)) return true
		if (this.active.size < this.limit) {
			this.active.add(token)
			return true
		}
		this.waiters.push({ token, grant })
		return false
	}

	/** 슬롯 반납. 상한에 여유가 있을 때만 대기열 선두에게 승계 (적응 축소 후 과도 상태 보호) */
	release(token: symbol): void {
		if (!this.active.delete(token)) {
			this.waiters = this.waiters.filter((waiter) => waiter.token !== token)
			return
		}
		if (this.active.size >= this.limit) return
		const next = this.waiters.shift()
		if (next) {
			this.active.add(next.token)
			next.grant()
		}
	}
}

const budgetManager = new VideoPlaybackBudgetManager()

/** 디코드 실패를 budget에 전파 — 동시 재생 상한을 적응적으로 축소한다 */
export function reportVideoDecodeFailure(): void {
	budgetManager.notifyDecodeFailure()
}

/**
 * 재생 슬롯 점유 훅. enabled 동안 슬롯을 요청하고 언마운트/비활성 시 반납한다.
 * @returns 슬롯 획득 여부 (enabled=false면 budget 미적용으로 간주하고 true)
 */
export function useVideoPlaybackSlot(enabled: boolean): boolean {
	const [granted, setGranted] = useState(false)

	useEffect(() => {
		if (!enabled) return
		const token = Symbol("video-playback-slot")
		setGranted(budgetManager.request(token, () => setGranted(true)))
		return () => {
			budgetManager.release(token)
			setGranted(false)
		}
	}, [enabled])

	return enabled ? granted : true
}

/**
 * 뷰포트 가시성 감지 훅. 카드가 30% 이상 보이면 재생을 시작하고,
 * 15% 미만으로 줄어들 때만 정지한다 (히스테리시스 — 가장자리에 걸친 카드는
 * 계속 재생, 경계 플래핑 방지).
 * rootMargin 확장은 쓰지 않는다 — 화면 밖 카드까지 슬롯을 선점해
 * 정작 보이는 카드가 poster로 멈추는 문제를 만들기 때문.
 * display:none(예: 반응형 숨김 카드)은 비가시로 판정된다.
 * @returns 가시 여부 (enabled=false 또는 IntersectionObserver 미지원 시 true)
 */
export function useInViewport(ref: RefObject<Element | null>, enabled: boolean): boolean {
	const [inViewport, setInViewport] = useState(false)
	const [unsupported, setUnsupported] = useState(false)

	useEffect(() => {
		if (!enabled) return
		const element = ref.current
		if (!element) return
		if (typeof IntersectionObserver === "undefined") {
			setUnsupported(true)
			return
		}

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					const ratio = entry.isIntersecting ? entry.intersectionRatio : 0
					setInViewport((prev) => {
						if (ratio >= VISIBILITY_PLAY_RATIO) return true
						if (ratio < VISIBILITY_STOP_RATIO) return false
						return prev // 15~30% 구간: 이전 상태 유지 (히스테리시스)
					})
				}
			},
			{ threshold: [VISIBILITY_STOP_RATIO, VISIBILITY_PLAY_RATIO] }
		)
		observer.observe(element)
		return () => observer.disconnect()
	}, [enabled, ref])

	if (!enabled || unsupported) return true
	return inViewport
}
